import { combineEpics, ofType } from 'redux-observable';
import { of, interval, concat } from 'rxjs';
import {
	switchMap,
	takeUntil,
	exhaustMap,
	timeout,
	map,
	catchError,
	mergeMap,
} from 'rxjs/operators';
import types from '../actions/actionTypes';

import {
	pollingSuccess,
	pollingFailed,
	mergePollingIntoEntries,
	getEntriesSuccess,
	getEntriesFailed,
} from '../actions/apiActions';

import { polling as pollingApi, getEntries } from '../services/api';

import { scrollToEntry } from '../actions/userActions';

import { shouldRenderNewEntries } from '../utils/utils';

export const startPollingEpic = ( action$, state$ ) =>
	action$.pipe(
		ofType( types.START_POLLING ),
		switchMap( () =>
			interval( state$.value.config.refresh_interval * 1000 ).pipe(
				takeUntil( action$.pipe( ofType( types.CANCEL_POLLING ) ) ),
				exhaustMap( () =>
					pollingApi(
						state$.value.polling.newestEntry.timestamp,
						state$.value.config
					).pipe(
						timeout( 10000 ),
						map( ( res ) =>
							pollingSuccess(
								res.response,
								shouldRenderNewEntries(
									// minPage, not page: under infinite scroll `page` is the
									// deepest page loaded, so scrolling at all would permanently
									// stop new entries rendering inline. The question this guard
									// means to ask is "are the newest entries on screen?" —
									// that's minPage === 1, plus its own scroll-position check.
									state$.value.pagination.minPage,
									state$.value.api.entries,
									state$.value.polling.entries
								)
							)
						),
						catchError( ( error ) => of( pollingFailed( error ) ) )
					)
				)
			)
		)
	);

export const mergePollingEpic = ( action$, state$ ) =>
	action$.pipe(
		ofType( types.MERGE_POLLING ),
		switchMap( () => {
			const { pagination, polling, config } = state$.value;
			const entries = Object.keys( polling.entries ).map(
				( key ) => polling.entries[ key ]
			);
			const pages = Math.max( pagination.pages, polling.pages );

			// minPage, not page: if the newest entries are already loaded we merge in
			// place, which keeps everything infinite scroll has appended. Testing
			// `page` here would refetch page 1 and throw away the reader's position
			// the moment they had scrolled at all. Refetching is still right when the
			// feed starts below page 1 (e.g. after jumping to a key event).
			if ( pagination.minPage === 1 ) {
				return concat(
					of( mergePollingIntoEntries( entries, pages ) ),
					of(
						scrollToEntry(
							`id_${ entries[ entries.length - 1 ].id }`
						)
					)
				);
			}

			return getEntries( 1, config, polling.newestEntry ).pipe(
				timeout( 10000 ),
				mergeMap( ( res ) =>
					concat(
						of( getEntriesSuccess( res.response, true ) ),
						of( scrollToEntry( `id_${ polling.newestEntry.id }` ) )
					)
				),
				catchError( ( error ) => of( getEntriesFailed( error ) ) )
			);
		} )
	);

export default combineEpics( startPollingEpic, mergePollingEpic );
